function productDetailsMedia(config, productId) {
  return {
    mediaFilterOptions: config.mediaFilterOptions || [],
    modelInfo: '',
    scrollAtStart: true,
    scrollAtEnd: false,
    showAnalyticsMessage: false,
    analyticsMessage: '',
    analyticsMessageDuration: 5000,
    analyticsMessageDelay: 1000,
    analyticsMinCount: 1,
    currentSlideId: null,
    currentSlideIndex: 0,
    isFavorited: false,

    // cached refs
    gallery: null,
    thumbs: null,
    slides: null,
    thumbLinks: null,
    thumbById: null,
    mediaIdsByFilter: null,
    activeFilters: {},
    scrollRaf: null,
    productId: productId,

    init() {
      const context = Alpine.store('pdp');
      Alpine.reactive(context);

      this.scheduleAnalyticsMessage();
      this.updateFavoriteState();

      this.gallery = this.$el.querySelector('#media-gallery');
      this.thumbs = this.$el.querySelector('#media-thumbnails');
      if (!this.gallery) return;

      this.slides = Array.from(this.gallery.children);
      this.thumbLinks = this.thumbs ? Array.from(this.thumbs.querySelectorAll('a[href^="#"]')) : [];

      const variantId = context.variantId;
      this.modelInfo = context.variantIdMetafieldMap[variantId]['pdp.model_info'] || '';

      this.thumbById = new Map();
      for (const a of this.thumbLinks) {
        const id = a.getAttribute('href')?.slice(1);
        if (id) this.thumbById.set(String(id), a);
      }

      // Build per-filter-option index for variant-driven filtering.
      this.mediaIdsByFilter = {};
      for (const optName of this.mediaFilterOptions) {
        const key = optName.toLowerCase();
        const map = new Map();
        for (const slide of this.slides) {
          const val = (slide.getAttribute(`data-filter-${key}`) || '').trim();
          if (!val) continue;
          if (!map.has(val)) map.set(val, new Set());
          map.get(val).add(String(slide.id));
        }
        this.mediaIdsByFilter[key] = map;
      }

      // Map each filter-option name → its index on a variant's options[]
      // array, so we can read v.options[idx] when the variant changes.
      const optionIndices = {};
      const productOptions = context.product?.options || [];
      for (const optName of this.mediaFilterOptions) {
        const key = optName.toLowerCase();
        const idx = productOptions.findIndex((o) => o.toLowerCase() === key);
        optionIndices[key] = idx !== -1 ? idx : null;
      }

      const applyFromVariant = (v) => {
        if (!v || !Array.isArray(v.options)) return;
        const filters = {};
        for (const optName of this.mediaFilterOptions) {
          const key = optName.toLowerCase();
          const idx = optionIndices[key];
          if (idx !== null && v.options[idx]) {
            filters[key] = String(v.options[idx]).trim().toLowerCase();
          }
        }
        this.applyFilters(filters);
      };

      applyFromVariant(context.getVariantById?.(context.variantId));

      // Re-filter only on actual variant change (not every reactive read).
      let lastAppliedVid = context.variantId;
      Alpine.effect(() => {
        const vid = context.variantId;
        this.modelInfo = context.variantIdMetafieldMap[vid]?.['pdp.model_info'] || '';
        if (vid !== lastAppliedVid) {
          lastAppliedVid = vid;
          applyFromVariant(context.getVariantById?.(vid));
        }
      });

      // Background-warm the visible colour's images so swapping between its
      // shots doesn't flash. Other colours are warmed on swatch hover/focus
      // (see pdp:swatch-prefetch below), not eagerly at load.
      this.scheduleImagePreload();

      // High-priority prefetch when the customer hovers/focuses a swatch
      // — by the time the click lands, the bitmap is decoded and ready.
      window.addEventListener('pdp:swatch-prefetch', (ev) => {
        const { optionName, optionValue } = ev.detail || {};
        if (!optionName || !optionValue) return;
        const map = this.mediaIdsByFilter[String(optionName).toLowerCase()];
        const ids = map?.get(String(optionValue).trim().toLowerCase());
        if (ids?.size) this.warmSlidesByIds(ids);
      });

      requestAnimationFrame(() => this.handleScroll());
    },

    applyFilters(filters) {
      this.activeFilters = filters;

      const activeEntries = [];
      for (const [key, value] of Object.entries(filters)) {
        const map = this.mediaIdsByFilter[key];
        if (!map || !map.has(value)) continue;
        activeEntries.push({ key, ids: map.get(value) });
      }

      for (const slide of this.slides) {
        const id = String(slide.id);
        // No active filter (e.g. media-gallery loaded before a variant
        // is set) → show everything. Otherwise, a slide must EXPLICITLY
        // tag every active filter and match it. Untagged slides (e.g.
        // generic images with no `| Color: …` in their alt text) get
        // hidden whenever a color is active — they could belong to any
        // color, so showing them risks displaying the wrong one.
        const show =
          activeEntries.length === 0 ||
          activeEntries.every(({ key, ids }) => slide.hasAttribute(`data-filter-${key}`) && ids.has(id));
        slide.toggleAttribute('hidden', !show);

        const thumb = this.thumbById.get(id);
        if (thumb) thumb.toggleAttribute('hidden', !show);
      }

      if (this.scrollRaf) cancelAnimationFrame(this.scrollRaf);
      this.scrollRaf = requestAnimationFrame(() => this.handleScroll());
    },

    // Fetch + decode an <img> off the main render path so the live one
    // can swap in without a network or decode hitch. Already-loaded
    // images still get pre-decoded for the first reveal.
    warmImage(img, priority) {
      if (!img || !img.src) return;
      if (img.complete && img.naturalWidth > 0) {
        if (typeof img.decode === 'function') img.decode().catch(() => {});
        return;
      }
      const preloader = new Image();
      if (img.srcset) preloader.srcset = img.srcset;
      if (img.sizes) preloader.sizes = img.sizes;
      if ('fetchPriority' in preloader) preloader.fetchPriority = priority;
      preloader.decoding = 'async';
      preloader.src = img.src;
      if (typeof preloader.decode === 'function') {
        preloader.decode().catch(() => {});
      }
    },

    // Background-warm the CURRENTLY VISIBLE variant's images two rAFs
    // after mount, so switching between that colour's shots doesn't flash.
    // Hidden (non-selected colour) slides are intentionally skipped — the
    // hover/focus/touch prefetch path (pdp:swatch-prefetch → warmSlidesByIds)
    // warms a colour's images just-in-time as the pointer lands on its
    // swatch, so we no longer eagerly fetch every colourway at load (that
    // was ~200 requests / ~13 MB for images the customer never sees).
    // fetchPriority 'low' keeps these from competing with the LCP image.
    scheduleImagePreload() {
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          if (!this.gallery) return;
          const imgs = this.gallery.querySelectorAll('.pdp-media__item:not([hidden]) img[data-media="image"]');
          for (const img of imgs) this.warmImage(img, 'low');
        })
      );
    },

    // Eagerly warm a specific color's slides. Triggered by the
    // hover/focus/touch prefetch path — the customer's pointer is
    // already over a swatch and a click is imminent, so high priority.
    warmSlidesByIds(ids) {
      for (const id of ids) {
        const slide = document.getElementById(id);
        const img = slide?.querySelector('img[data-media="image"]');
        this.warmImage(img, 'high');
      }
    },

    handleScroll() {
      const { scrollRoot } = this.$refs;
      if (!scrollRoot) throw new Error('Expected ref scrollRoot to be defined');

      const { scrollLeft, scrollWidth, clientWidth } = scrollRoot;
      const denom = scrollWidth - clientWidth;
      const progress = denom > 0 ? scrollLeft / denom : 0;
      this.$root.style.setProperty('--media-scroll-progress', progress.toString());
      this.scrollAtEnd = scrollLeft + clientWidth >= scrollWidth - 20;
      this.scrollAtStart = scrollLeft <= 20;

      const slides = this.slides.filter((el) => !el.hasAttribute('hidden'));
      const containerCenter = scrollRoot.scrollLeft + scrollRoot.clientWidth / 2;

      let closestSlide = null;
      let closestDistance = Infinity;

      slides.forEach((slide, index) => {
        const slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
        const distance = Math.abs(containerCenter - slideCenter);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestSlide = { slide, index };
        }
      });

      if (closestSlide) {
        this.currentSlideIndex = closestSlide.index;
        this.currentSlideId = closestSlide.slide.id;
      }
    },
    scrollBy(n) {
      const { scrollRoot } = this.$refs;
      if (!scrollRoot) throw new Error('Expected ref scrollRoot to be defined');
      const { clientWidth, scrollLeft, clientHeight, scrollTop, scrollWidth, scrollHeight } = scrollRoot;
      if (clientWidth < scrollWidth) {
        scrollRoot.scrollLeft = Math.max(Math.min(scrollLeft + n * clientWidth, scrollWidth), 0);
      } else if (clientHeight < scrollHeight) {
        scrollRoot.scrollTop = Math.max(Math.min(scrollTop + n * clientHeight, scrollHeight), 0);
      }
    },
    scrollToSlide(id) {
      const { scrollRoot } = this.$refs;
      const slide = scrollRoot?.children.namedItem(id);
      if (!(slide instanceof HTMLElement)) return;
      const { clientWidth, clientHeight, scrollWidth, scrollHeight } = scrollRoot;
      if (clientWidth < scrollWidth) {
        scrollRoot.scrollLeft = slide.offsetLeft;
      } else if (clientHeight < scrollHeight) {
        scrollRoot.scrollTop = slide.offsetTop;
      }
    },
    scheduleAnalyticsMessage() {
      const isMobile = !window.matchMedia('(min-width: 768px)').matches;
      if (!isMobile) return;

      const events = [
        ['checkout_completed', (count) => `${count} shoppers bought this product today.`],
        ['product_added_to_cart', (count) => `${count} shoppers added this to their bag today.`],
        ['product_viewed', (count) => `${count} shoppers viewed this product today.`],
      ];

      const showAfterDelay = () => {
        this.showAnalyticsMessage = true;
        setTimeout(() => {
          this.showAnalyticsMessage = false;
        }, this.analyticsMessageDuration);
      };

      const tryShowMessage = async () => {
        for (const [eventName, format] of events) {
          try {
            const { count } = await this.queryAnalytics(eventName, 1);
            if (count >= this.analyticsMinCount) {
              this.analyticsMessage = format(count);
              setTimeout(showAfterDelay, this.analyticsMessageDelay);
              return;
            }
          } catch (err) {
            console.error('Error fetching analytics data', err);
          }
        }
      };

      setTimeout(tryShowMessage, this.analyticsMessageDelay);
    },
    async queryAnalytics(eventName = 'product_added_to_cart', period = 1) {
      const url = new URL('', 'https://cozy-earth-api.fly.dev');
      url.pathname = `/v1/events/shopify/product-events/${productId}/${eventName}`;
      url.searchParams.set('period', period);
      const res = await fetch(url);
      if (!res.ok) {
        throw new Error(`Error fetching analytics data: ${res.statusText}`);
      }
      return res.json();
    },
    async toggleFavorite() {
      await this.$store.productInterests.toggleInterest(this.productId, 'favorite');
      await this.updateFavoriteState(this.productId);
    },
    async updateFavoriteState() {
      this.isFavorited = await this.$store.productInterests.hasInterest(this.productId, 'favorite');
    },
  };
}
